import { useState, useEffect, useMemo } from "react";
import { Sparkles, X, Search, Package, Receipt, Wand2, Minus, Plus, Info, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const QUOTE_GEN_PREFS_KEY = "sion_quote_generator_prefs";

const DURATION_OPTIONS = [
  { value: "ai", label: "Let AI decide" },
  { value: "1 hour", label: "1 hour" },
  { value: "2 hours", label: "2 hours" },
  { value: "3 hours", label: "3 hours" },
  { value: "4 hours (half day)", label: "4 hours (half day)" },
  { value: "6 hours", label: "6 hours" },
  { value: "1 day (8 hours)", label: "1 day (8 hours)" },
  { value: "1.5 days", label: "1.5 days" },
  { value: "2 days", label: "2 days" },
  { value: "3 days", label: "3 days" },
  { value: "4 days", label: "4 days" },
  { value: "5 days", label: "5 days" },
];

// London congestion zone postcode prefixes (covers the bulk of the CZ).
const CZ_POSTCODE_PREFIXES = [
  "EC1", "EC2", "EC3", "EC4",
  "WC1", "WC2",
  "W1",
  "SW1A", "SW1E", "SW1H", "SW1P", "SW1V", "SW1W", "SW1X", "SW1Y",
  "SE1 0", "SE1 1", "SE1 2", "SE1 3", "SE1 6", "SE1 7", "SE1 8", "SE1 9",
  "N1 9",
];
function detectCongestionZone(address) {
  if (!address) return false;
  const upper = address.toUpperCase();
  // Match prefix only at a word boundary (start of string or after non-letter) to avoid
  // false positives like "flat WC1 House" matching inside a non-postcode token.
  return CZ_POSTCODE_PREFIXES.some((prefix) => {
    const safe = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(^|[^A-Z0-9])${safe}`);
    return re.test(upper);
  });
}

const DEFAULT_PREFS = {
  contractors_per_action: 1,
  duration_per_action: "ai",
  include_materials: true,
  materials_tier: "mid-range",
  parking_enabled: false,
  parking_type: "Pay by Phone",
  parking_rate: 5,
  parking_hours: 4,
  waste_enabled: false,
  waste_size: "standard",
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(QUOTE_GEN_PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}
function savePrefs(p) {
  try {
    const { num_actions, ...rest } = p;
    localStorage.setItem(QUOTE_GEN_PREFS_KEY, JSON.stringify(rest));
  } catch (err) {
    console.error("Failed to persist quote generator prefs", err);
  }
}

function NumberStepper({ value, onChange, min = 1, max = 10, testId, locked = false }) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  return (
    <div className="inline-flex items-center gap-2">
      <button type="button" onClick={dec} disabled={locked || value <= min} className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-40" style={{ border: "1px solid var(--border)", background: "var(--surface)" }} data-testid={`${testId}-dec`}>
        <Minus className="w-3.5 h-3.5" />
      </button>
      <span className="font-mono text-base font-bold min-w-[28px] text-center" data-testid={`${testId}-value`}>{value}</span>
      <button type="button" onClick={inc} disabled={locked || value >= max} className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-40" style={{ border: "1px solid var(--border)", background: "var(--surface)" }} data-testid={`${testId}-inc`}>
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
      style={{ background: checked ? "#E05C2A" : "#CBD5E1" }}
      data-testid={testId}
      role="switch"
      aria-checked={checked}
    >
      <span className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" style={{ transform: checked ? "translateX(22px)" : "translateX(2px)" }} />
    </button>
  );
}

function TierCard({ active, title, subtitle, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 rounded-lg p-3 text-left transition-all"
      style={{
        border: active ? "2px solid #E05C2A" : "1px solid var(--border)",
        background: active ? "#FFF5F0" : "var(--surface)",
      }}
      data-testid={testId}
    >
      <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{title}</p>
      <p className="text-[11px] mt-1 leading-snug" style={{ color: "var(--text-muted)" }}>{subtitle}</p>
    </button>
  );
}

// 1. Smart Quote Generator — opens pre-generation settings panel.
export function AiQuoteGenerator({ clientType, jobDescription, photoUrls, onApply, address, singleAction = false, triggerLabel = "AI Generate", testId = "ai-quote-gen" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form state — defaults from localStorage + smart congestion detection from address
  const initialCongestion = useMemo(() => detectCongestionZone(address), [address]);
  const [prefs, setPrefs] = useState(() => loadPrefs());
  const [numActions, setNumActions] = useState(1);
  const [congestionEnabled, setCongestionEnabled] = useState(initialCongestion);
  const [congestionDetected, setCongestionDetected] = useState(initialCongestion);

  // Whenever the panel opens, reload prefs and recompute congestion detection.
  const handleOpen = () => {
    setPrefs(loadPrefs());
    setNumActions(1);
    const detected = detectCongestionZone(address);
    setCongestionDetected(detected);
    setCongestionEnabled(detected);
    setOpen(true);
  };

  // If singleAction prop is on, force numActions to 1 at all times.
  useEffect(() => { if (singleAction) setNumActions(1); }, [singleAction]);

  const updatePref = (key, value) => setPrefs((p) => {
    const next = { ...p, [key]: value };
    savePrefs(next);
    return next;
  });

  const handleGenerate = async () => {
    if (!(jobDescription || "").trim() && (!photoUrls || photoUrls.length === 0)) {
      toast.error("Add a job description or upload photos first");
      return;
    }
    setLoading(true);
    const settings = {
      num_actions: singleAction ? 1 : numActions,
      single_action_mode: singleAction,
      contractors_per_action: prefs.contractors_per_action,
      duration_per_action: prefs.duration_per_action,
      include_materials: prefs.include_materials,
      materials_tier: prefs.materials_tier,
      parking: prefs.parking_enabled ? { enabled: true, type: prefs.parking_type, rate: prefs.parking_rate, hours: prefs.parking_hours } : { enabled: false },
      congestion: congestionEnabled ? { enabled: true, days: 1 } : { enabled: false },
      waste_collection: prefs.waste_enabled ? { enabled: true, size: prefs.waste_size } : { enabled: false },
    };
    try {
      const resp = await api.post("/ai/generate-quote", {
        description: jobDescription || "",
        client_type: clientType,
        photo_urls: photoUrls || [],
        settings,
      });
      if (resp.data?.error) {
        toast.error("AI generation failed: " + resp.data.error);
        return;
      }
      await onApply(resp.data, { singleAction, settings });
      toast.success(singleAction ? "Service added" : "Quote generated and applied");
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("AI generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={handleOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors" style={{ background: "#F3E8FF", color: "#7C3AED" }} data-testid={testId}>
        <Sparkles className="w-3.5 h-3.5" /> {triggerLabel}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-h-[85vh] overflow-y-auto"
          style={{
            width: "min(600px, calc(100vw - 32px))",
            maxWidth: "calc(100vw - 32px)",
            background: "#ffffff",
            borderRadius: "12px",
            padding: "28px",
            zIndex: 999,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          }}
          data-testid="quote-generator-settings"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5" style={{ color: "#7C3AED" }} /> Quote Generator Settings</DialogTitle>
            <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Set your preferences before generating — takes 30 seconds.</p>
          </DialogHeader>

          <div className="space-y-5 py-3">
            {/* Field 1 — Number of actions */}
            <div>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                    {singleAction ? "Generating 1 action for this service." : "How many separate actions should this job be split into?"}
                  </p>
                  <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Each action becomes a separate numbered service line item on the quote.</p>
                </div>
                <NumberStepper value={singleAction ? 1 : numActions} onChange={setNumActions} min={1} max={10} testId="qg-num-actions" locked={singleAction} />
              </div>
            </div>

            {/* Field 2 — Contractors per action */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>How many contractors per action?</p>
                <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>This affects the labour cost calculation for each action.</p>
              </div>
              <NumberStepper value={prefs.contractors_per_action} onChange={(v) => updatePref("contractors_per_action", v)} min={1} max={6} testId="qg-contractors" />
            </div>

            {/* Field 3 — Duration */}
            <div>
              <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Estimated duration per action</p>
              <p className="text-[11px] mt-1 mb-2" style={{ color: "var(--text-muted)" }}>Select a duration if you already know the rough time required. Otherwise let the AI estimate from the job description.</p>
              <select
                value={prefs.duration_per_action}
                onChange={(e) => updatePref("duration_per_action", e.target.value)}
                className="w-full h-10 rounded-lg px-3 text-sm"
                style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
                data-testid="qg-duration"
              >
                {DURATION_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            </div>

            {/* Field 4 — Include materials */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Include materials in this quote?</p>
                <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Toggle off to generate labour-only services.</p>
              </div>
              <Toggle checked={prefs.include_materials} onChange={(v) => updatePref("include_materials", v)} testId="qg-materials-toggle" />
            </div>

            {/* Field 5/6 — Materials tier + SKU rule (only when materials ON) */}
            {prefs.include_materials && (
              <div className="space-y-3 pl-3" style={{ borderLeft: "2px solid var(--border)" }}>
                <div>
                  <p className="text-sm font-semibold mb-2" style={{ color: "var(--text-primary)" }}>Materials specification</p>
                  <div className="flex gap-2">
                    <TierCard active={prefs.materials_tier === "budget"} title="Budget" subtitle="Void properties, basic repairs, rental standard." onClick={() => updatePref("materials_tier", "budget")} testId="qg-tier-budget" />
                    <TierCard active={prefs.materials_tier === "mid-range"} title="Mid-Range" subtitle="Standard residential, letting agent works." onClick={() => updatePref("materials_tier", "mid-range")} testId="qg-tier-mid" />
                    <TierCard active={prefs.materials_tier === "premium"} title="Premium" subtitle="High end residential, commercial, client-supplied spec." onClick={() => updatePref("materials_tier", "premium")} testId="qg-tier-premium" />
                  </div>
                </div>
                <div className="flex gap-2 p-3 rounded-lg text-[11px]" style={{ background: "var(--bg)", color: "var(--text-muted)", border: "1px solid var(--border)" }} data-testid="qg-sku-info">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>Materials will be generated with supplier name and item description only. SKU codes will only be included if the item already exists in your materials catalogue with a verified SKU. AI-generated items will not have SKU codes to avoid incorrect references.</span>
                </div>
              </div>
            )}

            {/* Field 7 — Parking */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Include parking costs?</p>
                <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Uses your default parking type and daily rate.</p>
              </div>
              <Toggle checked={prefs.parking_enabled} onChange={(v) => updatePref("parking_enabled", v)} testId="qg-parking-toggle" />
            </div>

            {/* Field 8 — Congestion */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Include congestion charge?</p>
                <p className="text-[11px] mt-1" style={{ color: congestionDetected ? "#92400E" : "var(--text-muted)" }} data-testid="qg-congestion-indicator">
                  {congestionDetected ? "Address detected as inside congestion zone" : "Address outside congestion zone"}
                </p>
              </div>
              <Toggle checked={congestionEnabled} onChange={setCongestionEnabled} testId="qg-congestion-toggle" />
            </div>

            {/* Field 9 — Waste collection */}
            <div>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: "var(--text-primary)" }}><Truck className="w-3.5 h-3.5" />Waste collection required?</p>
                </div>
                <Toggle checked={prefs.waste_enabled} onChange={(v) => updatePref("waste_enabled", v)} testId="qg-waste-toggle" />
              </div>
              {prefs.waste_enabled && (
                <div className="mt-3 pl-3" style={{ borderLeft: "2px solid var(--border)" }}>
                  <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--text-muted)" }}>Waste collection size</p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => updatePref("waste_size", "standard")}
                      className="flex-1 rounded-full px-4 py-2 text-left transition-all"
                      style={{
                        border: prefs.waste_size === "standard" ? "2px solid #E05C2A" : "1px solid var(--border)",
                        background: prefs.waste_size === "standard" ? "#FFF5F0" : "var(--surface)",
                        minWidth: "160px",
                      }}
                      data-testid="qg-waste-standard"
                    >
                      <p className="text-sm font-semibold">Standard</p>
                      <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>£60 — small load, single skip bag or van load.</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePref("waste_size", "large")}
                      className="flex-1 rounded-full px-4 py-2 text-left transition-all"
                      style={{
                        border: prefs.waste_size === "large" ? "2px solid #E05C2A" : "1px solid var(--border)",
                        background: prefs.waste_size === "large" ? "#FFF5F0" : "var(--surface)",
                        minWidth: "160px",
                      }}
                      data-testid="qg-waste-large"
                    >
                      <p className="text-sm font-semibold">Large load</p>
                      <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>£100–£150 — multiple loads or large skip required.</p>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="qg-cancel">Cancel</Button>
            <Button onClick={handleGenerate} disabled={loading} className="gap-2" style={{ background: "#E05C2A", color: "#fff" }} data-testid="qg-generate">
              <Sparkles className="w-4 h-4" /> {loading ? "Generating..." : "Generate Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// 1b. AI Rewrite Description — with preview
export function AiRewriteButton({ text, onRewrite }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const rewrite = async () => {
    if (!text || !text.trim()) { toast.error("Write something first"); return; }
    setLoading(true);
    try {
      const resp = await api.post("/ai/rewrite-description", { text });
      setPreview(resp.data.text);
    } catch { toast.error("Rewrite failed"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={rewrite} disabled={loading} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors" style={{ background: "#F3E8FF", color: "#7C3AED" }} data-testid="ai-rewrite-btn">
        <Wand2 className="w-3 h-3" /> {loading ? "Rewriting..." : "AI Rewrite"}
      </button>
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5" style={{ color: "#7C3AED" }} /> AI Rewrite Preview</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Original</p>
              <p className="text-sm p-3 rounded-lg whitespace-pre-wrap" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>{text}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "#7C3AED" }}>Rewritten</p>
              <p className="text-sm p-3 rounded-lg whitespace-pre-wrap" style={{ background: "#F3E8FF", border: "1px solid #7C3AED", color: "var(--text-primary)" }}>{preview}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Reject</Button>
            <Button onClick={() => { onRewrite(preview); setPreview(null); toast.success("Description updated"); }} style={{ background: "#7C3AED", color: "#fff" }}>Accept Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// 1c. Per-service AI Rewrite — inline replace with 30s undo (no preview dialog)
export function ServiceRewriteButton({ text, onRewrite, serviceName, jobDescription, clientType, testId = "service-ai-rewrite" }) {
  const [loading, setLoading] = useState(false);
  const [previousText, setPreviousText] = useState(null);
  const [undoVisible, setUndoVisible] = useState(false);

  const rewrite = async () => {
    if (!text || !text.trim()) { toast.error("Write something first"); return; }
    setLoading(true);
    try {
      const resp = await api.post("/ai/rewrite-description", {
        text,
        service_name: serviceName || "",
        job_description: jobDescription || "",
        client_type: clientType || "",
      });
      const rewritten = resp.data?.text;
      if (!rewritten) { toast.error("Rewrite returned empty"); return; }
      setPreviousText(text);
      onRewrite(rewritten);
      setUndoVisible(true);
      toast.success("Description rewritten — Undo available for 30s");
      // Auto-hide undo after 30s
      setTimeout(() => { setUndoVisible(false); setPreviousText(null); }, 30000);
    } catch { toast.error("Rewrite failed"); }
    finally { setLoading(false); }
  };

  const undo = () => {
    if (previousText !== null) {
      onRewrite(previousText);
      setUndoVisible(false);
      setPreviousText(null);
      toast.success("Reverted to original");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={rewrite}
        disabled={loading}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors disabled:opacity-60"
        style={{ background: "#F3E8FF", color: "#7C3AED" }}
        data-testid={testId}
      >
        <Wand2 className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> {loading ? "Rewriting..." : "AI Rewrite"}
      </button>
      {undoVisible && (
        <button
          type="button"
          onClick={undo}
          className="text-[11px] font-medium ml-2"
          style={{ color: "#7C3AED" }}
          data-testid={`${testId}-undo`}
        >
          Undo
        </button>
      )}
    </>
  );
}

// 2. AI Email Drafter
export function AiEmailDrafter({ jobId, jobDetails, emailType = "custom", onApply }) {
  const [loading, setLoading] = useState(false);

  const draft = async () => {
    setLoading(true);
    try {
      const resp = await api.post("/ai/draft-email", { type: emailType, job_id: jobId, ...jobDetails });
      onApply(resp.data);
    } catch { toast.error("AI drafting failed"); }
    finally { setLoading(false); }
  };

  return (
    <button onClick={draft} disabled={loading} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg transition-colors" style={{ background: "#F3E8FF", color: "#7C3AED" }} data-testid="ai-draft-email">
      <Sparkles className="w-3 h-3" /> {loading ? "Drafting..." : "AI Draft"}
    </button>
  );
}

// 3. Job Summary Generator
export function AiJobSummary({ jobId, onResult }) {
  const [loading, setLoading] = useState(false);

  const summarise = async () => {
    setLoading(true);
    try {
      const resp = await api.post("/ai/summarise-job", { job_id: jobId });
      onResult(resp.data.summary);
      toast.success("Summary generated");
    } catch { toast.error("Failed to generate summary"); }
    finally { setLoading(false); }
  };

  return (
    <button onClick={summarise} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors" style={{ background: "#F3E8FF", color: "#7C3AED" }} data-testid="ai-summarise">
      <Sparkles className="w-3.5 h-3.5" /> {loading ? "Summarising..." : "AI Summary"}
    </button>
  );
}

// 4. Smart Search Assistant
export function AiSearchAssistant() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setAnswer("");
    try {
      const resp = await api.post("/ai/search", { query });
      setAnswer(resp.data.answer);
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors" style={{ background: "#F3E8FF", color: "#7C3AED" }} data-testid="ai-search-btn"><Sparkles className="w-3.5 h-3.5" /> AI Search</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5" style={{ color: "#7C3AED" }} /> AI Assistant</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ask anything... e.g. 'show all overdue invoices'" className="flex-1" onKeyDown={(e) => e.key === "Enter" && search()} />
              <Button onClick={search} disabled={loading} style={{ background: "#7C3AED", color: "#fff" }}><Search className="w-4 h-4" /></Button>
            </div>
            {loading && <p className="text-sm" style={{ color: "var(--text-muted)" }}>Thinking...</p>}
            {answer && <div className="rounded-lg p-4 text-sm whitespace-pre-wrap" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>{answer}</div>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// 6. Receipt Categoriser
export function AiReceiptCategoriser({ onApply }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const categorise = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const resp = await api.post("/ai/categorise-receipt", { text });
      setResult(resp.data);
    } catch { toast.error("Categorisation failed"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors" style={{ background: "#F3E8FF", color: "#7C3AED" }} data-testid="ai-receipt"><Sparkles className="w-3.5 h-3.5" /> AI Receipt</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5" style={{ color: "#7C3AED" }} /> AI Receipt Categoriser</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste receipt text or describe the purchase..." rows={4} className="w-full rounded-lg p-3 text-sm resize-y" style={{ border: "1px solid var(--border)" }} />
            <Button onClick={categorise} disabled={loading || !text.trim()} className="w-full gap-2" style={{ background: "#7C3AED", color: "#fff" }}><Sparkles className="w-4 h-4" /> {loading ? "Analysing..." : "Categorise"}</Button>
            {result && !result.error && (
              <div className="rounded-lg p-3 text-sm space-y-1" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Merchant</span><span className="font-semibold">{result.merchant}</span></div>
                <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Amount</span><span className="font-mono font-bold">£{(result.amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>VAT</span><span className="font-mono">£{(result.vat_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Category</span><span className="badge-pill badge-blue text-[10px]">{result.category}</span></div>
                <div><span style={{ color: "var(--text-muted)" }}>Description</span><p>{result.description}</p></div>
              </div>
            )}
          </div>
          {result && !result.error && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => { onApply(result); setOpen(false); setResult(null); setText(""); toast.success("Expense created"); }} style={{ background: "#7C3AED", color: "#fff" }}>Create Expense</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
